// Sprints: planning (Backlog page), the sprint views, reports and retrospectives, time logging, sprint settings.
// Counted strings use i18next plurals: the base key is the plural form, `_one` (and `_few` in Russian) the others.
const sprintsEn = {
  sprints: {
    sprint: "Sprint",
    backlog: "Backlog",
    backlogTitle: "Backlog",
    allSprints: "All sprints",
    activeSprints: "Active sprints",
    planASprint: "Plan a sprint",
    next: "next",
    state: { planned: "Planned", active: "Active", completed: "Completed" },
    unit: { pts: "{{n}} pts", tasks: "{{n}} tasks" },
    points: "Story points",
    hours: "Hours",
    estimate: "Estimate",
    changeEstimate: "Change estimate",
    changeSprint: "Change sprint",
    quickPicks: "Quick picks",
    pointsPlaceholder: "Any number",
    pointsInvalid: "Enter a number from 0 to 1000.",
    followsParent: "Follows parent",
    unestimated: "No estimate",
    loggedOfEstimate: "Logged / original estimate",
    notVerified: "Done, not verified",
    notVerifiedShort: "Not verified",
    notVerifiedHint: "Done, but its testing hasn't passed yet",
    nNotVerified: "{{n}} not verified",
    nRemaining: "{{n}} remaining",
    progress: "Sprint progress",
    progressText: "{{done}} / {{total}}",
    setCapacity: "Set capacity",
    capacity: "Capacity",
    forecast_one: "Forecast {{amount}} from the last sprint",
    forecast_few: "Forecast {{amount}} from the last {{count}} sprints",
    forecast: "Forecast {{amount}} from the last {{count}} sprints",
    forecastBasis: {
      person_days: "Completed work per available person-day in recent sprints, times this sprint's person-days",
      working_days: "Completed work per working day in recent sprints, times this sprint's working days",
    },
    overForecast: "+{{n}} over the forecast",
    overCapacityBy: "+{{n}} over capacity",
    overBoth: "Over the forecast and the capacity",
    daysAround: "{{available}}/{{working}} days",
    daysOffHint: "Working days around in this sprint (days off left out)",
    afterDaysOff: "{{capacity}} × {{available}}/{{working}} days = {{effective}}",
    daysOff: {
      title: "Days off",
      titleFor: "Days off — {{name}}",
      hint: "Vacations and sick days. They lower capacity in planned sprints of every project.",
      from: "From",
      to: "To",
      note: "Note",
      notePlaceholder: "Vacation",
      add: "Add days off",
      edit: "Edit",
      save: "Save",
      remove: "Remove",
      none: "No days off planned.",
      merged: "Overlapping days off are joined into one.",
      button: "Days off",
      nDays_one: "{{count}} day",
      nDays_few: "{{count}} days",
      nDays: "{{count}} days",
    },
    capacityOf: "{{total}} / {{capacity}}",
    over: "+{{n}} over",
    overCapacity: "Over capacity",
    overall: "Whole team",
    capacityFor: "Capacity for {{name}}",
    noCap: "No cap",
    capacityInvalid: "Capacities are numbers of 0 or more.",
    capacityHint: {
      points: "In story points. Leave a field blank for no cap.",
      hours: "In hours. Leave a field blank for no cap.",
      none: "In tasks. Leave it blank for no cap.",
    },
    perPerson: "Load per person",
    startsIn: "Starts in {{count}} days",
    startsIn_one: "Starts tomorrow",
    startsToday: "Starts today",
    daysLeft: "{{count}} days left",
    daysLeft_one: "{{count}} day left",
    lastDay: "Last day",
    endedAgo: "Ended {{count}} days ago",
    endedAgo_one: "Ended yesterday",
    nTasks: "{{count}} tasks",
    nTasks_one: "{{count}} task",
    nUnestimated: "{{count}} not estimated",
    nSelected: "{{count}} selected",
    doneOf: "{{done}} of {{total}} done",
    filtered: "filtered",
    groupBySprint: "By sprint",
    sortBySprint: "Sprint",

    // Backlog page
    createSprint: "Create sprint",
    selection: "Selected tasks",
    moveTo: "Move to",
    moveTaskTo: "Move {{key}} to…",
    selectTask: "Select {{key}}",
    clearSelection: "Clear selection",
    noSprintsTitle: "No sprints planned",
    noSprintsBody: "Create a sprint, then drag tasks into it from the backlog below.",
    noSprintsViewer: "A project manager plans sprints here.",
    emptySprint: "No tasks in this sprint yet.",
    emptySprintDrop: "Drag tasks here from the backlog, or use “Move to” on a task.",
    emptySprintMove: "Use “Move to” on a task to plan it into this sprint.",
    emptyBacklog: "The backlog is empty: every open task is in a sprint.",
    addGoal: "Add a goal",
    actionsFor: "More actions for {{name}}",
    edit: "Edit sprint",
    currentAndPlanned: "Current and planned",
    completedSprints: "Completed",
    openBacklog: "Open the backlog",

    // Dialogs
    name: "Name",
    goal: "Goal",
    goalHint: "What the team wants to achieve in this sprint.",
    startDate: "Start date",
    endDate: "End date",
    endBeforeStart: "The end date can't be before the start date.",
    create: "Create sprint",
    createTitle: "New sprint",
    editTitle: "Edit {{name}}",
    created: "{{name}} created",
    saved: "Sprint saved",
    start: "Start sprint",
    startTitle: "Start {{name}}",
    started: "{{name}} started",
    anotherActive: "{{name}} is still active. Complete it before starting another sprint.",
    emptyWarning: "This sprint has no tasks yet. You can still start it and add tasks later.",
    startSummary: "{{count}} tasks, {{total}} committed.",
    startSummary_one: "{{count}} task, {{total}} committed.",
    complete: "Complete sprint",
    completeTitle: "Complete {{name}}",
    completed: "{{name}} completed",
    allDone: "Every task in this sprint is done. Completing it freezes the report.",
    unfinishedIntro: "{{count}} tasks aren't finished:",
    unfinishedIntro_one: "{{count}} task isn't finished:",
    moveUnfinished: "Move unfinished tasks to",
    destNext: "Next sprint: {{name}}",
    destNextNone: "Next planned sprint",
    destNextNoneHint: "There's no planned sprint. Pick a new sprint instead.",
    destNew: "A new sprint",
    destNewHint: "Created now, right after this one.",
    destBacklog: "The backlog",
    destBacklogHint: "Out of any sprint, to plan again later.",
    subtasksFollow: "Subtasks go with their parent task.",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "Its {{count}} tasks go back to the backlog. This can't be undone.",
    deleteBody_one: "Its {{count}} task goes back to the backlog. This can't be undone.",
    deleted: "{{name}} deleted",

    // Sprint view
    completedBanner: "This sprint was completed on {{date}}. Its board is read-only.",
    openReport: "Open the report",
    openSprint: "Open the sprint",
    report: "Report",

    // Report
    reportTitle: "{{name}} report",
    tabReport: "Report",
    tabRetro: "Retrospective",
    reportPlannedTitle: "The report starts with the sprint",
    reportPlannedBody: "Once the sprint starts, its commitment, burndown and scope changes show here.",
    committed: "Committed",
    completedLabel: "Completed",
    completedSplit: "{{committed}} committed + {{added}} added",
    added: "Added",
    removed: "Removed",
    estimateChange: "Estimates {{n}}",
    notVerifiedTiles: "Done tasks whose testing hasn't passed",
    burndown: "Burndown",
    burndownPoints: "Story points left in open tasks per day.",
    burndownHours: "Remaining time per day.",
    burndownTasks: "Open tasks per day.",
    burndownDayOne: "Day one: {{initial}} to go. The line fills in day by day.",
    remainingLine: "Remaining",
    idealLine: "Ideal",
    scopeChange: "Scope change",
    listCompleted: "Completed",
    listNotCompleted: "Not completed",
    listRemoved: "Removed from the sprint",
    listAdded: "Added after the start",
    listNotVerified: "Done, not verified",
    listCarriedMore: "Carried over more than once",
    listEmpty: "None",
    addedMark: "added",
    stillHere: "in this sprint",
    timesCarried: "carried {{count}} times",
    timeByPerson: "Time logged by person",
    timeTotal: "{{logged}} logged against {{estimate}} estimated",
    noTimeLogged: "No time logged in this sprint.",
    velocity: "Velocity",
    velocityAverage: "Average completed: {{n}} (last 6 completed sprints)",
    velocityNone: "Velocity shows once sprints are completed.",
    velocityOne: "One completed sprint so far: velocity is clearer after a few more.",

    // Retrospective
    retro: { well: "What went well", improve: "What didn't", action: "Action items" },
    retroHint: { well: "Keep doing these.", improve: "What slowed the team down.", action: "Concrete next steps, each with an owner." },
    retroAdd: { well: "Something that went well…", improve: "Something that didn't…", action: "An action item…" },
    retroIntro: "Anyone on the team can add notes. Each change saves as you go, even after the sprint is completed.",
    retroReadOnly: "The team's notes on this sprint.",
    retroEmpty: "Nothing yet.",
    retroDelete: "Delete note",
    retroDone: "Done",
    retroOwner: "Assign an owner",
    retroNoOwner: "No owner",
  },
  timer: {
    section: "Time",
    original: "Original estimate",
    loggedLabel: "Logged",
    remaining: "Remaining",
    setByHand: "set by hand",
    setRemaining: "Set remaining time",
    automatic: "Automatic",
    barLabel: "{{logged}} logged, {{remaining}} remaining",
    subtaskNote: "Time on a subtask counts toward its parent's estimate.",
    logTime: "Log time",
    logTitle: "Log time on {{key}}",
    editTitle: "Edit time on {{key}}",
    stoppedTitle: "Timer stopped on {{key}}",
    stoppedBody: "The time is saved. Adjust it if needed.",
    keep: "Keep as is",
    duration: "Time spent",
    durationHint: "Like 2h 30m, 45m or 1.5h",
    durationInvalid: "Write it like 2h 30m, 45m or 1.5h.",
    durationRange: "Between 1 minute and 24 hours.",
    date: "Date",
    dateHint: "Today when left empty",
    note: "Note",
    logged: "{{time}} logged",
    entrySaved: "Time entry saved",
    editEntry: "Edit entry",
    deleteEntry: "Delete entry",
    deleteTitle: "Delete this time entry?",
    deleteBody: "{{time}} on {{date}} will be removed.",
    review: "Review",
    reviewHint: "A forgotten timer stopped itself after 12 hours. Check the time and save it.",
    start: "Start timer",
    stop: "Stop timer",
    startOn: "Start timer on {{key}}",
    stopOn: "Stop timer on {{key}}",
    running: "Timer running on {{key}}: {{time}}",
  },
  sprintSettings: {
    scrumTitle: "Scrum",
    scrumIntro: "Sprints, the backlog, estimates, time logging, planning poker, standup and days off.",
    scrumLabel: "Use scrum in this project",
    scrumHint: "Turning it off hides these and pauses automatic sprints. Nothing is deleted: it all comes back when you turn it on.",
    timezoneTitle: "Timezone",
    timezoneIntro: "Recurring tasks and flow charts use this timezone.",
    title: "Sprints",
    intro: "How this project estimates work and runs its sprints.",
    unit: "Estimate unit",
    units: { points: "Story points", hours: "Hours", none: "None (count tasks)" },
    unitHint: "Sprint totals, capacity and the burndown use this unit.",
    unitLocked: "Complete the active sprint to change the unit.",
    length: "Sprint length",
    weeks: "{{count}} weeks",
    weeks_one: "{{count}} week",
    timezone: "Timezone",
    timezoneHint: "Sprint days start and end at midnight here.",
    timezoneSuggested: "Taken from your browser. Save to apply it.",
    timezoneUnknown: "Pick a timezone from the list.",
    workingDays: "Working days",
    workingDaysHint: "The burndown's ideal line stays flat on other days.",
    workingDaysNone: "Pick at least one working day.",
    day: { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" },
    dayLong: { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" },
    timeLogging: "Time logging",
    timeLoggingHint: "Log time on tasks and run a timer. Remaining time drops as time is logged.",
    timeLoggingHoursOnly: "Available when the estimate unit is hours.",
    auto: "Automatic sprints",
    autoHint: "Mixla completes each sprint on its end date, moves unfinished tasks to the next one and starts it.",
    autoWeekday: "Sprints start on",
    autoLength: "Each sprint lasts {{count}} weeks (the sprint length above).",
    autoLength_one: "Each sprint lasts {{count}} week (the sprint length above).",
    autoWillPlan: "Saving plans {{name}} right away.",
    autoKeeps: "Mixla keeps the next sprint planned.",
    unsaved: "Unsaved changes",
  },
};

export default sprintsEn;
